/**
 * Flag resolution.
 *
 * Precedence, lowest to highest:
 *   1. registry default        (`FEATURE_FLAGS[key].defaultEnabled`)
 *   2. per-chain override      (`ChainConfig.tradingTypes`, trading verticals only)
 *   3. environment override    (`REACT_APP_FEATURE_<KEY>`)
 *   4. dev/QA runtime override (localStorage / `?ff=`, non-production only)
 *
 * Every layer is either explicitly configured or absent — a layer never guesses.
 * Resolution is a handful of comparisons over an 8-entry registry and is NOT
 * cached, so tests can mutate `process.env` freely and React callers can
 * `useMemo` at their own granularity.
 */
import { getChainConfig, getChainIdFromEnv } from "../chains/getters";
import { getFeatureFlagDevOverride } from "./devOverrides";
import { getFeatureFlagEnvOverride } from "./env";
import { getFeatureFlagDefinition, isTradingTypeKey } from "./registry";
import {
  FeatureFlagKey,
  FeatureFlagResolution,
  FeatureFlagSource,
  TradingTypeOverrides,
} from "./types";

/**
 * Per-chain trading-vertical overrides for the configured chain.
 *
 * Uses `getChainConfig` (returns `undefined` for an unconfigured chain) rather
 * than `getCurrentChainConfig` (which falls back to Sepolia): an unknown chain
 * must contribute no opinion, not another chain's opinion.
 *
 * Memoised on the raw env value because `isFeatureEnabled` runs in render paths
 * and `getChainIdFromEnv` both allocates and logs. Keying on the env string
 * keeps tests that mutate `REACT_APP_CHAIN_ID` correct.
 */
let cachedChainIdEnv: string | undefined;
let cachedOverrides: TradingTypeOverrides | undefined;
let hasCachedOverrides = false;

const getChainTradingTypeOverrides = (): TradingTypeOverrides | undefined => {
  const chainIdEnv = process.env.REACT_APP_CHAIN_ID;
  if (!hasCachedOverrides || chainIdEnv !== cachedChainIdEnv) {
    cachedChainIdEnv = chainIdEnv;
    cachedOverrides = getChainConfig(getChainIdFromEnv())?.tradingTypes;
    hasCachedOverrides = true;
  }
  return cachedOverrides;
};

const getChainOverride = (key: FeatureFlagKey): boolean | undefined =>
  isTradingTypeKey(key) ? getChainTradingTypeOverrides()?.[key] : undefined;

/** Resolve a flag and report which layer decided it. */
export const resolveFeatureFlag = (
  key: FeatureFlagKey
): FeatureFlagResolution => {
  let enabled = getFeatureFlagDefinition(key).defaultEnabled;
  let source = FeatureFlagSource.Default;

  const chainOverride = getChainOverride(key);
  if (chainOverride !== undefined) {
    enabled = chainOverride;
    source = FeatureFlagSource.Chain;
  }

  const envOverride = getFeatureFlagEnvOverride(key);
  if (envOverride !== undefined) {
    enabled = envOverride;
    source = FeatureFlagSource.Env;
  }

  const devOverride = getFeatureFlagDevOverride(key);
  if (devOverride !== undefined) {
    enabled = devOverride;
    source = FeatureFlagSource.DevOverride;
  }

  return { key, enabled, source };
};

/** Is this feature enabled for the current build? */
export const isFeatureEnabled = (key: FeatureFlagKey): boolean =>
  resolveFeatureFlag(key).enabled;
